import React from 'react';

import { Download as DownloadIcon, ErrorOutlineOutlined as InfoIcon } from '@mui/icons-material';
import { Alert, Box, Button, Divider, Grid, Typography } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';
import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { DateFormatPatterns } from '@clearverify/constants';
import { formatDate, noop } from '@clearverify/utils';

import {
  ActiveInactive,
  GetAnnualFreeReportDataDocument,
  useCreateVerificationRequestByEmployeeMutation,
  useGenerateAnnualFreeReportMutation,
  useGetAnnualFreeReportDataQuery,
  useGetEmployersListForVerificationRequestForMyVerifyQuery,
  VerificationRequestType,
} from 'src/api';
import { PdfRenderer } from 'src/components/common';
import { SelectField, TextField } from 'src/components/finalForm';
import { ModalId } from 'src/constants/modals';
import { useNotification } from 'src/hooks/app';
import { useScreenInfo } from 'src/hooks/common';
import { useCurrentUserEmployee } from 'src/hooks/employee';
import { useModal, useModalV2 } from 'src/hooks/shared';
import { EntityActionConfirmationModal } from 'src/modals/common';
import { GetWorkLetterModal } from 'src/modals/position';
import { theme } from 'src/theme';
import { SxProp } from 'src/types/mui';
import {
  buildEmployersListForVerificationRequest,
  convertEmployerListItemsToEmployerDataItems,
} from 'src/utils/employer';
import { downloadFileByDownloadUrl } from 'src/utils/file';

import { CONSUMER_MY_VERIFY_INITIAL_FORM_VALUES, MY_VERIFY_PDF_DOCUMENT } from './constants';
import {
  ConsumersMyVerifyFields,
  ConsumersMyVerifyValues,
  EmployeeMyVerifyFormForRequestProps,
  StatusForm,
} from './types';
import { ConsumerMyVerifyValidationSchema, VERIFICATION_TYPES_OPTIONS } from './utils';

export const EmployeeMyVerifyFormForRequest: React.FC<EmployeeMyVerifyFormForRequestProps> = ({
  verificationType,
  setVerificationType,
  setStatusForm,
}) => {
  const { createApolloContext } = useNotification();
  const { openModal } = useModalV2();
  const { isMobile } = useScreenInfo();

  const { openModal: openModalSecond } = useModal();

  const { consumer, loading: consumerLoading } = useCurrentUserEmployee();

  const { data: annualFreeReportData } = useGetAnnualFreeReportDataQuery();

  const [generateAnnualFreeReport] = useGenerateAnnualFreeReportMutation({
    refetchQueries: [GetAnnualFreeReportDataDocument],
    context: createApolloContext({
      errorMessage: 'Something went wrong during Report generation, try again later',
      successMessage: 'Your free Verification of Income Report has been generated successfully',
    }),
  });

  const [createVerificationRequest] = useCreateVerificationRequestByEmployeeMutation({
    context: createApolloContext({
      errorMessage: 'Something wrong with data',
      successMessage: 'The verification request has been sent successfully',
    }),
  });

  const { data: employersData, loading: employersDataLoading } =
    useGetEmployersListForVerificationRequestForMyVerifyQuery();

  const employersSelectOptions = React.useMemo(
    () =>
      buildEmployersListForVerificationRequest(
        employersData?.getEmployersListForVerificationRequestForMyVerify.employers,
      ),
    [employersData?.getEmployersListForVerificationRequestForMyVerify.employers],
  );

  const hasNoEmploymentData = !employersDataLoading && employersSelectOptions.length === 0;

  const onSubmit = React.useCallback(
    async (formValues: ConsumersMyVerifyValues) => {
      try {
        await createVerificationRequest({
          variables: {
            companyName: formValues.companyName,
            contactEmail: formValues.contactEmail,
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            companies: convertEmployerListItemsToEmployerDataItems(formValues.employers),
            noteToRecipient: formValues.noteToRecipient,
          },
        });
        setStatusForm(StatusForm.anotherRequest);
      } catch (error) {
        console.error(error);
      }
    },
    [createVerificationRequest, setStatusForm],
  );

  const handleGenerateWorkLetter = React.useCallback(() => {
    if (employersSelectOptions.length === 0) return;
    openModal(GetWorkLetterModal, {
      id: ModalId.GetWorkLetter,
    });
  }, [employersSelectOptions, openModal]);

  const handleDownloadReportExample = React.useCallback(() => {
    const docToDownload = MY_VERIFY_PDF_DOCUMENT[verificationType];
    if (docToDownload) {
      window.open(docToDownload, '_blank');
    }
  }, [verificationType]);

  const handleGenerateVoiPdfByPosition = React.useCallback(() => {
    // Every download goes through the mutation so the server can audit it, a re-download
    // included; the stored file only decides whether generation needs confirming first.
    const download = async (options?: Parameters<typeof generateAnnualFreeReport>[0]) => {
      const response = await generateAnnualFreeReport(options);
      const file = response.data?.generateAnnualFreeReport.file;
      if (file) {
        downloadFileByDownloadUrl(file.downloadUrl, file.filename);
      }
    };

    if (annualFreeReportData?.getAnnualFreeReportData?.file) {
      // Nothing is generated on this path, so the hook-level "generated" toast is replaced.
      void download({
        context: createApolloContext({
          errorMessage: 'Something went wrong during Report download, try again later',
        }),
      });
      return;
    }

    openModal(EntityActionConfirmationModal, {
      id: ModalId.EntityActionConfirmation,
      onSubmit: () => download(),
      action: 'generate',
      entity: 'this free Verification of Income Report',
    });
  }, [
    annualFreeReportData?.getAnnualFreeReportData?.file,
    createApolloContext,
    generateAnnualFreeReport,
    openModal,
  ]);

  const handleChangePdfType = React.useCallback(
    (value: VerificationRequestType) => {
      setVerificationType(value);
    },
    [setVerificationType],
  );

  const isAccountFrozen = React.useMemo(() => {
    return consumer?.freeze === ActiveInactive.Active;
  }, [consumer?.freeze]);

  return (
    <React.Fragment>
      {!consumerLoading && (
        <Alert
          variant="outlined"
          severity="info"
          sx={theme => ({
            m: isMobile ? 1 : theme.spacing(4, 4, 0, 4),
            color: 'primary.main',
            borderColor: 'primary.main',
          })}
        >
          Per the Fair Credit Reporting Act (FCRA) all consumers are entitled to one free disclosure
          every 12 months upon request from each nationwide credit bureau and from nationwide
          specialty consumer reporting agencies.
          {annualFreeReportData?.getAnnualFreeReportData?.createdAt &&
            `The report was updated as of ${formatDate(
              annualFreeReportData?.getAnnualFreeReportData?.createdAt,
              DateFormatPatterns.shortDateWithSlash,
            )}`}
          &nbsp;
          <Typography
            variant="body2"
            component="span"
            sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={handleGenerateVoiPdfByPosition}
          >
            Click here to download your free report
          </Typography>
        </Alert>
      )}

      <Grid container p={isMobile ? 1 : 4} spacing={2}>
        <Grid size={isMobile ? 12 : 5}>
          <Typography variant="h5" fontSize="24px">
            Send Verification
          </Typography>

          <Typography mt={1} variant="body1" color="text.secondary">
            To send your employment / wage verification, fill out the information below and select
            the type of verification you want to send
          </Typography>
        </Grid>

        <Grid size={isMobile ? 12 : 7}>
          {isMobile ? (
            <React.Fragment>
              <SelectField
                fullWidth
                input={{
                  name: 'Verification Type',
                  value: verificationType,
                  onChange: handleChangePdfType,
                  onBlur: noop,
                  onFocus: noop,
                }}
                meta={{}}
                label="Example Verification Type"
                options={VERIFICATION_TYPES_OPTIONS}
              />
              <Button
                onClick={handleDownloadReportExample}
                sx={{ p: 0, textTransform: 'none', pb: 3 }}
              >
                Download an example of the Report
              </Button>
            </React.Fragment>
          ) : (
            <Box display="flex" justifyContent="flex-end">
              <Box width="100%" maxWidth="300px">
                <SelectField
                  fullWidth
                  input={{
                    name: 'Verification Type',
                    value: verificationType,
                    onChange: handleChangePdfType,
                    onBlur: noop,
                    onFocus: noop,
                  }}
                  meta={{}}
                  label="Example Verification Type"
                  options={VERIFICATION_TYPES_OPTIONS}
                />
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>

      <Grid container px={isMobile ? 1 : 4} spacing={2}>
        <Grid size={isMobile ? 12 : 5}>
          <Form<ConsumersMyVerifyValues>
            onSubmit={onSubmit}
            initialValues={CONSUMER_MY_VERIFY_INITIAL_FORM_VALUES}
            validate={validateWithYupSchema(ConsumerMyVerifyValidationSchema)}
            subscription={{ submitting: true, valid: true, dirty: true }}
          >
            {({ submitting, valid, dirty }) => (
              <Grid container spacing={3}>
                {hasNoEmploymentData && (
                  <Grid size={12}>
                    <Alert severity="warning" variant="outlined">
                      It is not possible to send Verification because your employment information
                      has not been uploaded yet. You can complete this action after your employer
                      uploads your data to the platform.
                    </Alert>
                  </Grid>
                )}

                <Grid size={12}>
                  <Field
                    name={ConsumersMyVerifyFields.companyName}
                    label="Verifier's Company Name"
                    required
                    fullWidth
                    disabled={submitting || hasNoEmploymentData}
                    component={TextField}
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    name={ConsumersMyVerifyFields.firstName}
                    label="Verifier's First Name"
                    required
                    fullWidth
                    disabled={submitting || hasNoEmploymentData}
                    component={TextField}
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    name={ConsumersMyVerifyFields.lastName}
                    label="Verifier's Last Name"
                    required
                    fullWidth
                    disabled={submitting || hasNoEmploymentData}
                    component={TextField}
                  />
                </Grid>

                <Grid size={12}>
                  <Field
                    name={ConsumersMyVerifyFields.contactEmail}
                    label="Email Address"
                    required
                    fullWidth
                    disabled={submitting || hasNoEmploymentData}
                    component={TextField}
                  />
                </Grid>

                <Grid size={12}>
                  <Field
                    name={ConsumersMyVerifyFields.employers}
                    label="Employers"
                    required
                    fullWidth
                    disabled={submitting || hasNoEmploymentData}
                    component={SelectField}
                    multiple
                    options={employersSelectOptions}
                  />
                </Grid>

                <Grid size={12}>
                  <Field
                    name={ConsumersMyVerifyFields.noteToRecipient}
                    label="Note to Recipient (optional)"
                    required={false}
                    fullWidth
                    disabled={submitting || hasNoEmploymentData}
                    component={TextField}
                  />
                </Grid>

                {isAccountFrozen ? (
                  <Grid size={12}>
                    <Grid container sx={containerStyles} justifyContent="space-between">
                      <Grid container direction="column" width="auto">
                        <Grid
                          container
                          direction="row"
                          wrap="nowrap"
                          gap={1.5}
                          color={theme.palette.action.active}
                        >
                          <InfoIcon />
                          <Grid container gap={0.5} direction="column" wrap="nowrap">
                            <Box>
                              <Typography
                                fontWeight={500}
                                color={theme.palette.secondary.contrastText}
                              >
                                Attention
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              color={theme.palette.secondary.contrastText}
                            >
                              You have frozen your account and cannot send verifications until you
                              have unlocked your account. Click on the button to unfreeze your
                              account, then try again.
                            </Typography>
                          </Grid>
                          <Box>
                            <Button
                              color="secondary"
                              size="small"
                              loading={consumerLoading}
                              onClick={() =>
                                openModalSecond(ModalId.UnfreezeYourAccountModal, { consumer })
                              }
                            >
                              Unfreeze
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid size={12}>
                    <Box display="flex" gap={3} justifyContent="center">
                      <Button
                        disabled={!valid || !dirty || submitting || hasNoEmploymentData}
                        loading={submitting || consumerLoading}
                        variant="contained"
                        type="submit"
                        size="large"
                      >
                        Send Verification
                      </Button>
                    </Box>
                  </Grid>
                )}

                <Grid size={12}>
                  <Divider>or</Divider>
                  <Box display="flex" gap={3} justifyContent="center" mt={4}>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="large"
                      disabled={hasNoEmploymentData}
                      loading={employersDataLoading}
                      endIcon={<DownloadIcon />}
                      onClick={handleGenerateWorkLetter}
                    >
                      Get Work Letter
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Form>
        </Grid>

        <Grid size={isMobile ? 12 : 7}>
          {!isMobile ? (
            <PdfRenderer renderMode="canvas" src={MY_VERIFY_PDF_DOCUMENT[verificationType]} />
          ) : null}
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

const containerStyles: SxProp = theme => ({
  padding: 2,
  border: `1px solid ${theme.palette.action.active}`,
  borderRadius: 2,
  backgroundColor: theme.palette.common.white,
});
